import { canWriteControls, hasModuleAccess } from "../../accessControl";
import { useDraftDirty, useUnsavedChanges } from "../../components/common/UnsavedChangesBoundary";
import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import Loader from "../../components/common/Loader";
import EmptyState from "../../components/common/EmptyState";
import DestructiveConfirm from "../../components/common/DestructiveConfirm";
import { ControlsService } from "../../api/generated";
import { withApi, withApiData } from "../../api/openapiClient";
import { useToast } from "../../components/common/ToastProvider";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Select from "../../ui/Select";
import Textarea from "../../ui/Textarea";
import RelatedKpisPanel from "../../components/kpi/RelatedKpisPanel";
import PinButton from "../../components/pins/PinButton";
import GovernanceSection from "../../components/controls/GovernanceSection";
import ControlRisksPanel from "../../components/controls/ControlRisksPanel";
import ControlReviewNoteModal from "../../components/controls/ControlReviewNoteModal";
import EvidenceUploader from "../../components/EvidenceUploader";
import EvidenceList from "../../components/EvidenceList";
import EvidenceAssociations from "../../components/EvidenceAssociations";
import { colors, spacing } from "../../ui/tokens";
import { useAuth } from "../../useAuth";
import useRegisterRecentView from "../../features/recentViews/useRegisterRecentView";
import { listControlAssignmentUsers } from "../../api/controls.api.js";
import { listTaxonomyValues } from "../../api/taxonomy.api";
import { getControlReviewTemplate } from "../../api/controlReviewTemplate.api";
import {
  pageHeaderActionsStyle,
  pageHeaderStyle,
  pageHeaderTitleBlockStyle,
  pageShellStyle,
  pageSubtitleStyle,
  pageTitleStyle,
  surfaceCardBodyStyle,
  surfaceCardStyle,
  toolbarStyle,
} from "../../ui/pagePatterns";
import {
  buildControlReviewNote,
  hasControlReviewChanges,
  renderControlReviewNoteFromTemplate,
} from "../../utils/controlReviewNote";
import { normalizeControlEffectivenessKey } from "../../utils/controlEffectiveness";

const CONTROL_LABEL_OPTIONS = ["Preventative", "Detective", "Corrective"];

const normalizeDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const normalizeText = (value) => {
  if (value === null || value === undefined) return "";
  return String(value);
};

const normalizeControlLabels = (value) => {
  if (!value) return [];
  const source = Array.isArray(value) ? value : String(value).split(";");
  const normalized = [];
  source.forEach((item) => {
    const label = String(item || "").trim();
    if (!CONTROL_LABEL_OPTIONS.includes(label)) return;
    if (!normalized.includes(label)) {
      normalized.push(label);
    }
  });
  return normalized;
};

const buildControlState = (control) => ({
  name: normalizeText(control?.name || "").trim(),
  description: normalizeText(control?.description || ""),
  category: normalizeText(control?.category || "").trim(),
  control_labels: normalizeControlLabels(control?.control_labels),
  status: normalizeText(control?.status || "").trim(),
  owner_id: normalizeText(control?.owner_id || "").trim(),
  assignee_id: normalizeText(control?.assignee_id || "").trim(),
  owner_user_id: normalizeText(control?.owner_user_id || "").trim(),
  assignee_user_id: normalizeText(control?.assignee_user_id || "").trim(),
  catalog_id: normalizeText(control?.catalog_id || control?.catalogId || "").trim(),
  review_cadence: normalizeText(
    control?.review_cadence || control?.reviewCadence || control?.review_frequency || ""
  ).trim(),
  last_review_date: normalizeDateInput(
    control?.last_review_date || control?.lastReviewDate || control?.last_reviewed_at || ""
  ),
  last_reviewed_by: normalizeText(control?.last_reviewed_by || control?.lastReviewedBy || "").trim(),
  review_outcome: normalizeText(control?.review_outcome || control?.reviewOutcome || "").trim(),
  effectiveness_rating:
    normalizeControlEffectivenessKey(
      control?.effectiveness_rating || control?.effectiveness || control?.control_effectiveness || "",
      { blankKey: "" }
    ) || normalizeText(control?.effectiveness_rating || control?.effectiveness || control?.control_effectiveness || "").trim(),
  evidence_expectation: normalizeText(control?.evidence_expectation || control?.evidenceExpectation || "").trim(),
  control_category_id: normalizeText(control?.control_category_id || control?.controlCategoryId || "").trim(),
});

const formatPreviewValue = (value, fallback = "Not set") => {
  if (value === null || value === undefined || String(value).trim() === "") return fallback;
  return String(value);
};

const formatPreviewDate = (value, fallback = "Not set") => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString();
};

const infoLabelStyle = {
  fontSize: 12,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontWeight: 600,
};

const infoValueStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: "#111827",
};

const tabRowStyle = {
  ...toolbarStyle,
  borderBottom: `1px solid ${colors.border}`,
  marginBottom: spacing.md,
};

const panelShellStyle = {
  ...surfaceCardStyle,
  display: "grid",
};

const panelBodyStyle = {
  ...surfaceCardBodyStyle,
  display: "grid",
  gap: spacing.md,
};

const buildUserOptions = (items, currentValues = []) => {
  const options = [];
  const seen = new Set();
  const list = Array.isArray(items) ? items : [];
  list.forEach((user) => {
    const value = user?.id != null ? String(user.id) : "";
    if (!value || seen.has(value)) return;
    const label = user?.username
      ? `${user.username}${user.email ? ` (${user.email})` : ""}`
      : user?.email || `User #${user.id}`;
    seen.add(value);
    options.push({ value, label });
  });
  const selectedValues = Array.isArray(currentValues) ? currentValues : [currentValues];
  selectedValues.forEach((currentValue) => {
    const value = currentValue ? String(currentValue) : "";
    if (!value || seen.has(value)) return;
    seen.add(value);
    options.unshift({ value, label: `Unavailable user #${value}` });
  });
  return options;
};

const filterOptions = (options, query) => {
  const term = String(query || "").trim().toLowerCase();
  if (!term) return options;
  return options.filter((opt) => String(opt.label || opt.value || "").toLowerCase().includes(term));
};

const resolveOptionLabel = (options, value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  const match = options.find((opt) => String(opt.value) === String(value));
  return match?.label || fallback || String(value);
};

const labelChipWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xs,
};

const labelChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  border: `1px solid ${colors.border}`,
  borderRadius: 999,
  padding: "2px 8px",
  fontSize: 12,
  color: colors.text,
  background: colors.surfaceSubtle,
};


export default function ControlDetail({ controlId, embedded = false, onClose }) {
  const { cid: cidParam } = useParams();
  const cid = controlId ?? cidParam;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  const { user } = useAuth() || {};

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [auditView, setAuditView] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [evidenceReloadKey, setEvidenceReloadKey] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [controlDraft, setControlDraft] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saveError, setSaveError] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [userSearchOwner, setUserSearchOwner] = useState("");
  const [userSearchAssignee, setUserSearchAssignee] = useState("");
  const [userError, setUserError] = useState("");
  const [controlCategories, setControlCategories] = useState([]);
  const [categoryError, setCategoryError] = useState("");
  const [reviewNoteModalOpen, setReviewNoteModalOpen] = useState(false);
  const [reviewNoteDraft, setReviewNoteDraft] = useState("");
  const [reviewNoteError, setReviewNoteError] = useState("");
  const [pendingReviewPayload, setPendingReviewPayload] = useState(null);
  const [reviewTemplate, setReviewTemplate] = useState(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["control", cid],
    queryFn: () =>
      withApiData(
        ControlsService.getControlControlsControlIdGet({
          controlId: Number(cid),
        })
      ),
    enabled: Boolean(cid),
  });

  const {
    data: history,
    isLoading: historyLoading,
    isError: historyError,
    error: historyErr,
  } = useQuery({
    queryKey: ["controlHistory", cid],
    queryFn: () =>
      withApiData(
        ControlsService.getControlHistoryControlsControlIdHistoryGet({
          controlId: Number(cid),
        })
      ),
    enabled: Boolean(cid),
  });

  const {
    data: tests,
    isLoading: testsLoading,
    isError: testsError,
    error: testsErr,
  } = useQuery({
    queryKey: ["controlTests", cid],
    queryFn: () =>
      withApiData(
        ControlsService.listControlTestsControlsControlIdTestsGet({
          controlId: Number(cid),
        })
      ),
    enabled: Boolean(cid),
  });

  const controlBase = useMemo(() => buildControlState(data), [data]);
  const controlValues = controlDraft || controlBase;
  useRegisterRecentView({
    itemType: "control",
    itemId: data?.id || cid,
    title: data?.name,
    secondaryText: data?.category || data?.status || null,
    enabled: Boolean(data?.id && data?.name),
  });

  const normalizedStatus = String(data?.status || "").trim().toLowerCase();
  const isDraft = normalizedStatus === "draft";
  const isArchived = normalizedStatus === "archived";
  const isOwner =
    (Boolean(user?.id) && [data?.owner_user_id, data?.assignee_user_id].some(
      value => value && String(value) === String(user.id))) ||
    (Boolean(user?.username) && [data?.owner_id, data?.assignee_id].includes(user.username));
  const canReadEvidence = hasModuleAccess(user, "controls", "VIEW") &&
    (user?.role === "ADMIN" || isOwner);
  const canEditControl = canWriteControls(user);
  const canDeleteControl = hasModuleAccess(user, "controls", "MANAGE") && user?.role === "ADMIN";
  const requestClose = useUnsavedChanges(
    useDraftDirty(controlDraft, isEditing)
  );

  const editEnabled = isEditing && !auditView && !isArchived && canEditControl;

  useEffect(() => {
    if (!data) return;
    if (!isEditing) {
      setControlDraft(buildControlState(data));
      setFieldErrors({});
      setSaveError("");
    }
  }, [data, isEditing]);

  useEffect(() => {
    if (auditView && isEditing) {
      setIsEditing(false);
      setControlDraft(buildControlState(data));
      setFieldErrors({});
      setSaveError("");
    }
  }, [auditView, isEditing, data]);

  useEffect(() => {
    let active = true;

    const loadReviewTemplate = async ({ force = false } = {}) => {
      try {
        const res = await getControlReviewTemplate({ force });
        if (!active) return;
        setReviewTemplate(res?.data || null);
      } catch (_err) {
        if (!active) return;
        setReviewTemplate(null);
      }
    };

    const handleTemplateChange = () => {
      loadReviewTemplate({ force: true });
    };

    loadReviewTemplate();
    window.addEventListener("controlReviewTemplateChanged", handleTemplateChange);

    return () => {
      active = false;
      window.removeEventListener("controlReviewTemplateChanged", handleTemplateChange);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const loadCategories = async () => {
      setCategoryError("");
      try {
        const res = await listTaxonomyValues("control_category");
        const payload = res?.data || res;
        if (active) {
          setControlCategories(Array.isArray(payload) ? payload : []);
        }
      } catch (err) {
        if (active) {
          setControlCategories([]);
          setCategoryError(err?.response?.data?.detail || err?.message || "Unable to load categories.");
        }
      }
    };
    loadCategories();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!data?.id || !canEditControl) return;
    let active = true;
    const loadReviewers = async () => {
      setUserError("");
      try {
        const res = await listControlAssignmentUsers();
        const payload = res?.data || [];
        const list = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.items)
            ? payload.items
            : [];
        const userOpts = buildUserOptions(list, [data?.owner_user_id, data?.assignee_user_id]);
        if (active) {
          setUserOptions(userOpts);
        }
      } catch (err) {
        if (active) {
          setUserOptions(buildUserOptions([], ""));
          setUserError(err?.detail || err?.message || "Unable to load users.");
        }
      }
    };
    loadReviewers();
    return () => {
      active = false;
    };
  }, [data?.id, data?.owner_user_id, data?.assignee_user_id, canEditControl]);

  const handleAuditToggle = () => {
    setAuditView((prev) => !prev);
  };

  const handleDraftChange = (field, value) => {
    setControlDraft((prev) => ({
      ...(prev || controlBase),
      [field]: value,
    }));
    setFieldErrors((prev) => {
      if (!prev?.[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setSaveError("");
  };

  const controlChanges = useMemo(() => {
    const fields = [
      "name",
      "description",
      "category",
      "control_labels",
      "control_category_id",
      "status",
      "owner_id",
      "assignee_id",
      "owner_user_id",
      "assignee_user_id",
      "catalog_id",
      "review_cadence",
      "last_review_date",
      "last_reviewed_by",
      "review_outcome",
      "effectiveness_rating",
      "evidence_expectation",
    ];
    const diff = {};
    fields.forEach((field) => {
      const fromValue = controlBase?.[field] ?? "";
      const toValue = controlValues?.[field] ?? "";
      if (String(fromValue) !== String(toValue)) {
        diff[field] = { from: fromValue, to: toValue };
      }
    });
    return diff;
  }, [controlBase, controlValues]);

  const validationErrors = useMemo(() => {
    const errors = {};
    if (!isEditing || !controlValues) return errors;
    if (!controlValues.name) {
      errors.name = "Name is required.";
    }
    const lastReviewDate = controlValues.last_review_date;
    if (lastReviewDate) {
      const date = new Date(lastReviewDate);
      if (Number.isNaN(date.getTime())) {
        errors.last_review_date = "Invalid review date.";
      } else if (date > new Date()) {
        errors.last_review_date = "Review date cannot be in the future.";
      }
    }
    if (controlValues.review_outcome || controlValues.effectiveness_rating) {
      if (!controlValues.last_review_date) {
        errors.last_review_date = "Provide last review date with outcome or effectiveness.";
      }
      if (!controlValues.last_reviewed_by) {
        errors.last_reviewed_by = "Provide reviewer when setting outcome or effectiveness.";
      }
    }
    if (isDraft) {
      const restricted = ["last_review_date", "last_reviewed_by", "review_outcome", "effectiveness_rating"];
      const restrictedChange = restricted.some((field) => controlChanges[field]);
      if (restrictedChange) {
        errors.governance = "Review fields are available once the control is Active.";
      }
    }
    return errors;
  }, [controlValues, isEditing, isDraft, controlChanges]);

  const combinedErrors = { ...validationErrors, ...fieldErrors };
  const hasChanges = Object.keys(controlChanges).length > 0;
  const saveDisabled =
    !isEditing ||
    !canEditControl ||
    auditView ||
    isArchived ||
    !hasChanges ||
    Object.keys(combinedErrors).length > 0;

  const statusOptions = useMemo(() => {
    const base = [
      { value: "", label: "Not set" },
      { value: "Draft", label: "Draft" },
      { value: "Active", label: "Active" },
      { value: "Archived", label: "Archived" },
      { value: "Open", label: "Open" },
      { value: "In Progress", label: "In Progress" },
      { value: "Closed", label: "Closed" },
    ];
    const current = controlValues?.status || "";
    if (current && !base.some((opt) => opt.value === current)) {
      base.push({ value: current, label: current });
    }
    return base;
  }, [controlValues?.status]);

  const activeCategoryOptions = useMemo(() => {
    const list = Array.isArray(controlCategories) ? controlCategories : [];
    const active = list.filter((item) => String(item?.status || "active").toLowerCase() === "active");
    const currentId = controlValues?.control_category_id;
    const current =
      currentId && !active.some((item) => String(item.id) === String(currentId))
        ? list.find((item) => String(item.id) === String(currentId))
        : null;
    const combined = current ? [current, ...active] : active;
    return combined.map((item) => ({
      value: String(item.id),
      label: item.label || item.value || `Category #${item.id}`,
    }));
  }, [controlCategories, controlValues?.control_category_id]);

  const ownerOptions = useMemo(() => {
    const base = Array.isArray(userOptions) ? userOptions : [];
    return [{ value: "", label: "Unassigned" }, ...base];
  }, [userOptions]);

  const filteredOwnerOptions = useMemo(
    () => filterOptions(ownerOptions, userSearchOwner),
    [ownerOptions, userSearchOwner]
  );

  const filteredAssigneeOptions = useMemo(
    () => filterOptions(ownerOptions, userSearchAssignee),
    [ownerOptions, userSearchAssignee]
  );

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      withApiData(
        ControlsService.updateControlControlsControlIdPut({
          controlId: Number(cid),
          requestBody: payload,
        })
      ),
    onSuccess: (result) => {
      const updated = result?.data || result;
      success("Control updated");
      queryClient.invalidateQueries({ queryKey: ["control", cid] });
      queryClient.invalidateQueries({ queryKey: ["controlHistory", cid] });
      if (updated) {
        queryClient.setQueryData(["control", cid], updated);
        setControlDraft(buildControlState(updated));
      }
      setIsEditing(false);
      setFieldErrors({});
      setSaveError("");
      setReviewNoteModalOpen(false);
      setReviewNoteDraft("");
      setReviewNoteError("");
      setPendingReviewPayload(null);
    },
    onError: (err) => {
      setFieldErrors(err?.fieldErrors || {});
      const message = err?.detail || err?.message || "Failed to update control";
      setSaveError(message);
      if (reviewNoteModalOpen || pendingReviewPayload) {
        setReviewNoteError(message);
      }
    },
  });

  const buildSavePayload = () => {
    const payload = {};
    Object.keys(controlChanges).forEach((field) => {
      let value = controlValues?.[field];
      if (field === "last_review_date") {
        value = value ? new Date(value).toISOString() : null;
      }
      if (["owner_user_id", "assignee_user_id", "control_category_id"].includes(field)) {
        if (value === "" || value === null || value === undefined) {
          value = null;
        } else {
          const parsed = Number(value);
          value = Number.isNaN(parsed) ? null : parsed;
        }
      }
      if (value === "") value = null;
      payload[field] = value;
    });
    return payload;
  };

  const closeReviewNoteModal = () => {
    if (saveMutation.isLoading) return;
    setReviewNoteModalOpen(false);
    setReviewNoteDraft("");
    setReviewNoteError("");
    setPendingReviewPayload(null);
  };

  const handleSave = () => {
    if (saveDisabled || reviewNoteModalOpen) return;
    const payload = buildSavePayload();
    if (hasControlReviewChanges(controlChanges)) {
      setPendingReviewPayload(payload);
      const nextReviewNote = reviewTemplate?.template_body
        ? renderControlReviewNoteFromTemplate({
            templateBody: reviewTemplate.template_body,
            control: data,
            changes: controlChanges,
            nextValues: controlValues,
            currentValues: controlBase,
          })
        : buildControlReviewNote({
            changes: controlChanges,
            nextValues: controlValues,
            currentValues: controlBase,
          });
      setReviewNoteDraft(nextReviewNote);
      setReviewNoteError("");
      setSaveError("");
      setReviewNoteModalOpen(true);
      return;
    }
    saveMutation.mutate(payload);
  };

  const handleConfirmReviewNote = () => {
    if (saveMutation.isLoading || !pendingReviewPayload) return;
    const trimmedNote = String(reviewNoteDraft || "").trim();
    if (!trimmedNote) {
      setReviewNoteError("Provide a review note.");
      return;
    }
    setReviewNoteError("");
    setSaveError("");
    saveMutation.mutate({
      ...pendingReviewPayload,
      review_note: trimmedNote,
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setControlDraft(buildControlState(data));
    setFieldErrors({});
    setSaveError("");
    closeReviewNoteModal();
  };

  const deleteMutation = useMutation({
    mutationFn: () =>
      withApi(
        ControlsService.deleteControlControlsControlIdDelete({
          controlId: Number(cid),
        })
      ),
    onSuccess: () => {
      success("Control deleted");
      queryClient.invalidateQueries({ queryKey: ["controls"] });
      navigate("/controls");
      if (typeof onClose === "function") {
        onClose();
      }
    },
    onError: (err) => showError(err?.detail || "Failed to delete control"),
  });

  const breadcrumbs = [
    { label: "Controls", to: "/controls" },
    { label: data?.name || "Detail" },
  ];

  const shellStyle = embedded ? { display: "grid", gap: spacing.md } : pageShellStyle;
  const numericControlId = Number(cid);
  const hasControlId = Number.isInteger(numericControlId) && numericControlId > 0;

  return (
    <div style={shellStyle}>
      {!embedded && <Breadcrumbs items={breadcrumbs} />}
      {isLoading && <Loader />}
      {isError && <div>Error: {error?.detail || "Failed to load control"}</div>}
      {(data || !isLoading) && (
        <div style={{ display: "grid", gap: spacing.md }}>
          <div style={pageHeaderStyle}>
            <div style={pageHeaderTitleBlockStyle}>
              <h1 style={pageTitleStyle}>{controlValues?.name || data?.name || "Control details"}</h1>
              <p style={pageSubtitleStyle}>Review governance, evidence, tests, and change history.</p>
            </div>
            <div style={pageHeaderActionsStyle}>
              {embedded && hasControlId && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    if (typeof onClose === "function") {
                      onClose();
                    }
                    navigate(`/controls/${numericControlId}`);
                  }}
                >
                  Open Full Page
                </Button>
              )}
              {!embedded && hasControlId && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate("/controls", { state: { openDrawerControlId: numericControlId } })}
                >
                  Minimize to Side Panel
                </Button>
              )}
              {!embedded && hasControlId && (
                <PinButton
                  itemType="control"
                  itemId={numericControlId}
                  summary={{
                    title: controlValues?.name || data?.name || "Control details",
                    secondaryText: [controlValues?.category || data?.category, controlValues?.assignee_id || data?.assignee_id]
                      .filter(Boolean)
                      .join(" | "),
                    status: controlValues?.status || data?.status || null,
                    ownerDisplay: controlValues?.owner_id || data?.owner_id || null,
                    updatedAt: data?.updated_at || null,
                  }}
                />
              )}
              <Button
                type="button"
                data-testid="audit-view-toggle"
                onClick={handleAuditToggle}
                variant="secondary"
              >
                {auditView ? "Exit Audit View" : "Audit View"}
              </Button>
              {!isEditing && (
                <Button
                  type="button"
                  data-testid="edit-control"
                  onClick={() => setIsEditing(true)}
                  variant="secondary"
                  disabled={!data || auditView || !canEditControl || isArchived}
                >
                  Edit
                </Button>
              )}
              {isEditing && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={requestClose || handleCancel}
                    disabled={saveMutation.isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleSave}
                    disabled={saveDisabled || saveMutation.isLoading}
                  >
                    {saveMutation.isLoading ? "Saving..." : "Save"}
                  </Button>
                </>
              )}
              <Button
                type="button"
                data-testid="delete-control"
                onClick={() => setConfirmOpen(true)}
                variant="destructive"
                disabled={!data || !canDeleteControl}
              >
                Delete
              </Button>
              {typeof onClose === "function" && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={requestClose || onClose}
                  style={{ border: "none", background: "transparent", fontSize: 18, padding: "0 6px" }}
                  aria-label="Close control drawer"
                >
                  X
                </Button>
              )}
            </div>
          </div>
          <div style={panelShellStyle}>
            <div style={{ ...surfaceCardBodyStyle, paddingBottom: 0 }}>
              <div style={tabRowStyle}>
                {[
                  { key: "overview", label: "Overview" },
                  { key: "evidence", label: "Evidence" },
                  { key: "tests", label: "Tests" },
                  { key: "history", label: "History" },
                ].map((tab) => (
                  <Button
                    key={tab.key}
                    type="button"
                    variant="ghost"
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: `${spacing.sm}px ${spacing.md}px`,
                      borderBottom: activeTab === tab.key ? `2px solid ${colors.accent}` : "2px solid transparent",
                      borderRadius: 0,
                      color: activeTab === tab.key ? colors.accent : "#475467",
                      boxShadow: "none",
                    }}
                  >
                    {tab.label}
                  </Button>
                ))}
              </div>
            </div>

          {activeTab === "overview" && (
            <div style={panelBodyStyle}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: spacing.md,
                }}
              >
                {editEnabled ? (
                  <Input
                    label="Name"
                    value={controlValues?.name || ""}
                    onChange={(e) => handleDraftChange("name", e.target.value)}
                    error={combinedErrors.name}
                  />
                ) : (
                  <div>
                    <div style={infoLabelStyle}>Name</div>
                    <div style={infoValueStyle}>{formatPreviewValue(controlValues?.name)}</div>
                  </div>
                )}
                {editEnabled ? (
                  <Select
                    label="Status"
                    value={controlValues?.status || ""}
                    onChange={(e) => handleDraftChange("status", e.target.value)}
                    options={statusOptions}
                  />
                ) : (
                  <div>
                    <div style={infoLabelStyle}>Status</div>
                    <div style={infoValueStyle}>{formatPreviewValue(controlValues?.status)}</div>
                  </div>
                )}
                {editEnabled ? (
                  <Select
                    label="Control Category"
                    value={controlValues?.control_category_id || ""}
                    onChange={(e) => handleDraftChange("control_category_id", e.target.value)}
                    options={activeCategoryOptions}
                    emptyLabel={categoryError || "No active categories"}
                  />
                ) : (
                  <div>
                    <div style={infoLabelStyle}>Control Category</div>
                    <div style={infoValueStyle}>
                      {resolveOptionLabel(
                        activeCategoryOptions,
                        controlValues?.control_category_id,
                        controlValues?.category || "Not set"
                      )}
                    </div>
                  </div>
                )}
                {editEnabled ? (
                  <div style={{ display: "grid", gap: spacing.xs }}>
                    <div style={infoLabelStyle}>Control Labels</div>
                    <div style={labelChipWrapStyle}>
                      {CONTROL_LABEL_OPTIONS.map((label) => {
                        const selected = Array.isArray(controlValues?.control_labels)
                          ? controlValues.control_labels.includes(label)
                          : false;
                        return (
                          <label key={label} style={{ ...labelChipStyle, cursor: "pointer", userSelect: "none" }}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => {
                                const current = Array.isArray(controlValues?.control_labels)
                                  ? controlValues.control_labels
                                  : [];
                                const next = selected
                                  ? current.filter((item) => item !== label)
                                  : [...current, label];
                                handleDraftChange("control_labels", next);
                              }}
                            />
                            <span>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={infoLabelStyle}>Control Labels</div>
                    {Array.isArray(controlValues?.control_labels) && controlValues.control_labels.length > 0 ? (
                      <div style={labelChipWrapStyle}>
                        {controlValues.control_labels.map((label) => (
                          <span key={label} style={labelChipStyle}>
                            {label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div style={infoValueStyle}>Not set</div>
                    )}
                  </div>
                )}
                {editEnabled ? (
                  <div style={{ display: "grid", gap: 6 }}>
                    <Input
                      label="Owner search"
                      value={userSearchOwner}
                      onChange={(e) => setUserSearchOwner(e.target.value)}
                      placeholder="Search users..."
                    />
                    <Select
                      label="Owner"
                      value={controlValues?.owner_user_id || ""}
                      onChange={(e) => handleDraftChange("owner_user_id", e.target.value)}
                      options={filteredOwnerOptions}
                      emptyLabel={userError || "No users available"}
                    />
                  </div>
                ) : (
                  <div>
                    <div style={infoLabelStyle}>Owner</div>
                    <div style={infoValueStyle}>
                      {resolveOptionLabel(
                        ownerOptions,
                        controlValues?.owner_user_id,
                        controlValues?.owner_id || "Not set"
                      )}
                    </div>
                  </div>
                )}
                {editEnabled ? (
                  <div style={{ display: "grid", gap: 6 }}>
                    <Input
                      label="Assignee search"
                      value={userSearchAssignee}
                      onChange={(e) => setUserSearchAssignee(e.target.value)}
                      placeholder="Search users..."
                    />
                    <Select
                      label="Assignee"
                      value={controlValues?.assignee_user_id || ""}
                      onChange={(e) => handleDraftChange("assignee_user_id", e.target.value)}
                      options={filteredAssigneeOptions}
                      emptyLabel={userError || "No users available"}
                    />
                  </div>
                ) : (
                  <div>
                    <div style={infoLabelStyle}>Assignee</div>
                    <div style={infoValueStyle}>
                      {resolveOptionLabel(
                        ownerOptions,
                        controlValues?.assignee_user_id,
                        controlValues?.assignee_id || "Not set"
                      )}
                    </div>
                  </div>
                )}
                {editEnabled ? (
                  <Input
                    label="Catalog ID"
                    value={controlValues?.catalog_id || ""}
                    onChange={(e) => handleDraftChange("catalog_id", e.target.value)}
                  />
                ) : (
                  <div>
                    <div style={infoLabelStyle}>Catalog ID</div>
                    <div style={infoValueStyle}>
                      {formatPreviewValue(controlValues?.catalog_id)}
                    </div>
                  </div>
                )}
                <div style={{ gridColumn: "1 / -1" }}>
                  {editEnabled ? (
                    <Textarea
                      label="Description"
                      value={controlValues?.description || ""}
                      onChange={(e) => handleDraftChange("description", e.target.value)}
                      rows={3}
                    />
                  ) : (
                    <div>
                      <div style={infoLabelStyle}>Description</div>
                      <div style={infoValueStyle}>{formatPreviewValue(controlValues?.description)}</div>
                    </div>
                  )}
                </div>
              </div>

              <ControlRisksPanel control={data} editing={editEnabled} onChange={async () => {
                await Promise.all(["control", "controlHistory", "controls", "risk", "risks", "riskControls", "pinnedItems"].map(key =>
                  queryClient.invalidateQueries({ queryKey: [key] })
                ));
              }} />

              <section aria-label="Linked Actions" style={{ display: "grid", gap: spacing.xs }}>
                <div style={infoLabelStyle}>Linked Actions</div>
                {(data?.actions || []).map((action) => (
                  <div key={action.id}>
                    <Link reloadDocument to={`/actions/${action.id}`}>{action.title || action.name}</Link>
                    <span> — {action.status || "Status not set"}</span>
                  </div>
                ))}
                {!data?.actions?.length && <div>No accessible linked actions</div>}
              </section>

              <div style={{ marginTop: "16px" }}>
                <GovernanceSection
                  data={controlValues}
                  isEditing={isEditing}
                  isAuditView={auditView}
                  status={data?.status}
                  canEdit={canEditControl}
                  onChange={handleDraftChange}
                  errors={combinedErrors}
                />
                {combinedErrors.governance && (
                  <div style={{ marginTop: 8, color: "#b91c1c", fontSize: 12 }}>
                    {combinedErrors.governance}
                  </div>
                )}
                {isEditing && hasChanges && (
                  <div
                    style={{
                      marginTop: 16,
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      padding: 12,
                      background: "#f9fafb",
                      display: "grid",
                      gap: 8,
                    }}
                  >
                    <div style={{ fontWeight: 600 }}>Change Preview</div>
                    {(() => {
                      const reviewFields = ["last_review_date", "last_reviewed_by", "review_outcome"];
                      const reviewChanges = reviewFields.filter((field) => controlChanges[field]);
                      const items = [];
                      if (reviewChanges.length > 0) {
                        items.push({
                          label: "Review update",
                          changes: reviewChanges,
                        });
                      }
                      ["review_cadence", "effectiveness_rating", "evidence_expectation"].forEach((field) => {
                        if (controlChanges[field]) {
                          items.push({ label: field, changes: [field] });
                        }
                      });
                      [
                        "name",
                        "description",
                        "category",
                        "control_labels",
                        "control_category_id",
                        "status",
                        "owner_user_id",
                        "assignee_user_id",
                        "catalog_id",
                      ].forEach((field) => {
                        if (controlChanges[field]) {
                          items.push({ label: field, changes: [field] });
                        }
                      });
                      return items.map((item) => (
                        <div key={item.label} style={{ display: "grid", gap: 4 }}>
                          <div style={{ fontWeight: 600, textTransform: "capitalize" }}>
                            {item.label.replace(/_/g, " ")}
                          </div>
                          {item.changes.map((field) => {
                            const change = controlChanges[field];
                            const resolveVal = (val) => {
                              if (field === "last_review_date") {
                                return formatPreviewDate(val);
                              }
                              if (field === "owner_user_id" || field === "assignee_user_id") {
                                return resolveOptionLabel(ownerOptions, val, "Not set");
                              }
                              if (field === "control_category_id") {
                                return resolveOptionLabel(activeCategoryOptions, val, "Not set");
                              }
                              if (field === "control_labels") {
                                const labels = normalizeControlLabels(val);
                                return labels.length ? labels.join(", ") : "Not set";
                              }
                              return formatPreviewValue(val);
                            };
                            const fromVal = resolveVal(change.from);
                            const toVal = resolveVal(change.to);
                            return (
                              <div key={field} style={{ fontSize: 12, color: "#475467" }}>
                                {field.replace(/_/g, " ")}: {fromVal} -> {toVal}
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}
                    {saveError && (
                      <div style={{ color: "#b91c1c", fontSize: 12 }}>{saveError}</div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ marginTop: "12px" }}>
                <RelatedKpisPanel entityType="control" entityId={cid} />
              </div>
            </div>
          )}

          {activeTab === "evidence" && (
            <div style={panelBodyStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>Evidence</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Upload files or add URLs for audit support.</div>
                </div>
              </div>
              {canReadEvidence && canEditControl && !isArchived && !auditView && (
                <EvidenceUploader parentType="control" parentId={cid} onCreated={() => setEvidenceReloadKey((key) => key + 1)} />
              )}
              {canReadEvidence && <EvidenceAssociations parentType="control" parentId={cid} writable={canEditControl && !isArchived && !auditView} />}
              {canReadEvidence ? <EvidenceList key={evidenceReloadKey} parentType="control" parentId={cid} /> :
                <div>Evidence is unavailable with your current access.</div>}
            </div>
          )}

          {activeTab === "tests" && (
            <div style={panelBodyStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontWeight: 600 }}>Tests</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>Recorded tests for this control.</div>
                </div>
                <Button type="button" variant="secondary" disabled>
                  Add Test
                </Button>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Coming soon (backend support required).</div>
              {testsLoading && <Loader label="Loading tests..." />}
              {testsError && (
                <div style={{ color: "#b91c1c" }}>
                  {testsErr?.detail || testsErr?.message || "Failed to load tests"}
                </div>
              )}
              {!testsLoading && !testsError && Array.isArray(tests) && tests.length === 0 && (
                <div style={{ color: "#6b7280" }}>No tests recorded.</div>
              )}
              {!testsLoading && !testsError && Array.isArray(tests) && tests.length > 0 && (
                <div style={{ display: "grid", gap: spacing.sm }}>
                  {tests.map((test) => (
                    <div
                      key={test.id}
                      style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: spacing.sm }}
                    >
                      <div style={{ fontWeight: 600 }}>
                        {test.date ? new Date(test.date).toLocaleDateString() : "No date"}{" "}
                        {test.performed_by ? `- ${test.performed_by}` : ""}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>Result: {test.result || "Not set"}</div>
                      {test.notes && <div style={{ marginTop: 4 }}>{test.notes}</div>}
                      {Array.isArray(test.evidence_links) && test.evidence_links.length > 0 && (
                        <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
                          Evidence links: {test.evidence_links.join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "history" && (
            <div style={panelBodyStyle}>
              <div style={{ fontWeight: 600 }}>History</div>
              {historyLoading && <Loader label="Loading history..." />}
              {historyError && (
                <div style={{ color: "#b91c1c" }}>
                  {historyErr?.detail || historyErr?.message || "Failed to load history"}
                </div>
              )}
              {!historyLoading && !historyError && Array.isArray(history) && history.length === 0 && (
                <div style={{ color: "#6b7280" }}>No history recorded.</div>
              )}
              {!historyLoading && !historyError && Array.isArray(history) && history.length > 0 && (
                <div style={{ display: "grid", gap: spacing.sm }}>
                  {history.map((entry) => (
                    <div
                      key={entry.id}
                      style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: spacing.sm }}
                    >
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : "Timestamp unavailable"} -{" "}
                        {entry.changed_by || "System"}
                      </div>
                      <div style={{ fontWeight: 600 }}>{entry.field || "Update"}</div>
                      {(entry.from_value || entry.to_value) && (
                        <div style={{ fontSize: 12, color: "#475467" }}>
                          {entry.from_value || "-"} -> {entry.to_value || "-"}
                        </div>
                      )}
                      {entry.note && <div style={{ marginTop: 4 }}>{entry.note}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      )}
      {!isLoading && !data && <EmptyState title="Control not found" />}
      <DestructiveConfirm
        actionId="CONTROL_DELETE"
        open={confirmOpen}
        resourceDisplayName={data?.name}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          if (canDeleteControl) deleteMutation.mutate();
        }}
      />
      <ControlReviewNoteModal
        open={reviewNoteModalOpen}
        saving={saveMutation.isLoading}
        note={reviewNoteDraft}
        error={reviewNoteError}
        onChange={(nextValue) => {
          setReviewNoteDraft(nextValue);
          if (reviewNoteError) setReviewNoteError("");
        }}
        onCancel={closeReviewNoteModal}
        onConfirm={handleConfirmReviewNote}
      />
    </div>
  );
}
